import { useSyncExternalStore } from 'react';

import type { MiniAppActionState, MiniAppContext } from '../../types';

import {
  getTasksPanelSnapshot,
  subscribeTasksPanel,
} from './tasks-panel-store';

/**
 * Drives the Tasks action button from the panel-store snapshot (the panel-state
 * hook is the poller — this hook fetches nothing):
 *
 *  - marker `generating` → spinner (extraction in flight);
 *  - marker terminal, or any tasks visible (legacy marker-less visits) →
 *    count pill, an explicit 0 included ("we looked, found none");
 *  - no marker and nothing extracted → hidden (extraction was never requested:
 *    old visit, flag off, or the note isn't done yet).
 */
export function useTasksActionState(ctx: MiniAppContext): MiniAppActionState {
  const snapshot = useSyncExternalStore(
    subscribeTasksPanel,
    getTasksPanelSnapshot,
  );
  // No published state for THIS session yet (panel not mounted / first fetch
  // still in flight / snapshot is a previous appointment's) → keep hidden.
  if (!ctx.appointmentId || snapshot.appointmentId !== ctx.appointmentId) {
    return { action: { hidden: true } };
  }
  if (snapshot.extraction === 'generating') {
    return { action: { loading: true } };
  }
  const total =
    (snapshot.counts?.pending ?? 0) + (snapshot.counts?.accepted ?? 0);
  if (
    total > 0 ||
    snapshot.extraction === 'done' ||
    snapshot.extraction === 'failed'
  ) {
    return { action: { badge: total } };
  }
  return { action: { hidden: true } };
}
